import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BsChevronLeft, BsChevronRight, BsGraphUp, BsBoxArrowRight, BsPerson, BsGear } from 'react-icons/bs';
import { menuItems } from './menuItems';
import './Sidebar.css';
import logo from '../../assets/img/h-letter.svg';
import avatar from '../../assets/img/avatar.png';
import { AuthContext } from '../../App';

const MenuItem = ({ item, showTooltip, hideTooltip, isActive }) => {
  const Icon = item.icon;
  const hasSubItems = item.subItems && item.subItems.length > 0;
  
  return (
    <>
      <li className={`${isActive(item.path) ? 'active' : ''} ${hasSubItems ? 'parent' : ''}`}>
        <Link 
          to={item.path}
          onMouseEnter={(e) => showTooltip(e, item.title)}
          onMouseLeave={hideTooltip}
        >
          <Icon size={18} />
          <span>{item.title}</span>
          {item.badge && (
            <span className="badge">
              {item.badge.text}
            </span>
          )}
        </Link>
        {hasSubItems && (
          <div className="submenu">
            {item.subItems.map((subItem, index) => (
              <li 
                key={index}
                className={`submenu-item ${isActive(subItem.path) ? 'active' : ''}`}
              >
                <Link 
                  to={subItem.path}
                  onMouseEnter={(e) => showTooltip(e, subItem.title)}
                  onMouseLeave={hideTooltip}
                >
                  <subItem.icon size={16} />
                  <span>{subItem.title}</span>
                  {subItem.badge && (
                    <span className="badge">
                      {subItem.badge.text}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </div>
        )}
      </li>
    </>
  );
};

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const userProfileRef = useRef(null);
  const { logout } = React.useContext(AuthContext);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    setShowDropdown(false);
    logout();
    console.log('Çıkış yapılıyor...');
    setTimeout(() => {
      navigate('/giris-yap');
    }, 100);
  };

  const showTooltip = useCallback((e, text) => {
    if (!isOpen) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({
        visible: true,
        text,
        x: rect.right + 15,
        y: rect.top + rect.height / 2
      });
    }
  }, [isOpen]);

  const hideTooltip = useCallback(() => {
    setTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  useEffect(() => {
    if (isOpen) {
      hideTooltip();
    }
  }, [isOpen, hideTooltip]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        userProfileRef.current &&
        !userProfileRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActive = (path) => {
    const currentPath = location.pathname;
    const normalizedCurrentPath = currentPath
      .toLowerCase()
      .replace(/ü/g, 'u')
      .replace(/ğ/g, 'g')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ş/g, 's')
      .replace(/ç/g, 'c');

    const normalizedPath = path
      .toLowerCase()
      .replace(/ü/g, 'u')
      .replace(/ğ/g, 'g')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ş/g, 's')
      .replace(/ç/g, 'c');

    if (normalizedPath === '/' && normalizedCurrentPath === '/') {
      return true;
    }
    if (normalizedPath !== '/') {
      return normalizedCurrentPath === normalizedPath || 
             normalizedCurrentPath.startsWith(normalizedPath + '/');
    }
    return false;
  };

  return (
    <>
      <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img src={logo} className='logo' alt="Logo"/>
            <span className="logo-text">Hedef İzleme</span>
          </div>
          <button 
            className="toggle-btn" 
            onClick={toggleSidebar}
            aria-label={isOpen ? 'Sidebar\'ı Kapat' : 'Sidebar\'ı Aç'}
          >
            {isOpen ? <BsChevronLeft size={18} /> : <BsChevronRight size={18} />}
          </button>
        </div>

        <div className="sidebar-content">
          {menuItems.map((section, index) => (
            <div key={index} className="menu-section">
              <div className="sidebar-section-title">{section.title}</div>
              <ul className="menu-items">
                {section.items.map((item, itemIndex) => (
                  <MenuItem 
                    key={itemIndex}
                    item={item}
                    showTooltip={showTooltip}
                    hideTooltip={hideTooltip}
                    isActive={isActive}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="user-profile-wrapper">
            <div 
              ref={userProfileRef}
              className="user-profile" 
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <img src={avatar} alt="Kullanıcı" className="user-avatar" />
              <div className="user-info">
                <span className="user-name">Prof. Dr. Lorem Ipsum</span>
                <span className="user-role">Dekan</span>
              </div>
            </div>

            {showDropdown && (
              <div ref={dropdownRef} className="user-dropdown">
                <Link to="/profil" className="dropdown-item">
                  <BsPerson />
                  Profil
                </Link>
                <Link to="/ayarlar" className="dropdown-item">
                  <BsGear />
                  Ayarlar
                </Link>
                <div className="dropdown-divider"></div>
                <button 
                  onClick={() => {
                    handleLogout();
                  }} 
                  className="dropdown-item text-danger"
                >
                  <BsBoxArrowRight />
                  Çıkış Yap
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="tooltip-container">
        {tooltip.visible && (
          <div 
            className={`tooltip ${tooltip.visible ? 'visible' : ''}`}
            style={{
              left: `${tooltip.x}px`,
              top: `${tooltip.y}px`,
              transform: `translate(0, -50%)`
            }}
          >
            {tooltip.text}
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar; 